import React, { useState, useEffect, useMemo } from "react";
import styled from "@emotion/styled";
import { NavLink } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { DataGrid, GridToolbar } from "@mui/x-data-grid";

import {
  Box,
  Button,
  Grid,
  IconButton,
  Breadcrumbs as MuiBreadcrumbs,
  Card as MuiCard,
  CardContent as MuiCardContent,
  Typography,
  Divider,
  Chip,
  Tooltip,
  CircularProgress,
} from "@mui/material";
import { spacing } from "@mui/system";
import { useTheme } from "@mui/material/styles";
import VisibilityIcon from "@mui/icons-material/Visibility";
import WarningIcon from "@mui/icons-material/Warning";
import ErrorIcon from "@mui/icons-material/Error";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { Link, useNavigate } from "react-router-dom";
import apiUtils from "../../utils/apiUtils";
import DataGridExport from "../../components/DataGridExport";
import axios from "axios";
import { Tabs, Tab } from "@mui/material";
import { AddCustomerDialog } from "./addcustomer";
import { usePermissions } from "../../hooks/usePermissions";
import { CAPABILITIES } from "../../auth/permissions";
// import { array } from "prop-types";

const Breadcrumbs = styled(MuiBreadcrumbs)(spacing);

const Card = styled(MuiCard)(spacing);

const CardContent = styled(MuiCardContent)(spacing);

const getCustomerOverviewPath = (row) =>
  `/customers/customeroverview?customerId=${row.entityId}&customerNumber=${row.entityNumber}`;

const CustomerDataGrid = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { hasPermission } = usePermissions();
  const canCreateCustomer = hasPermission(CAPABILITIES.customersCreate, "W");
  const [tableData, setTableData] = useState([]);
  const [activeTab, setActiveTab] = useState(null);
  const [documentStatuses, setDocumentStatuses] = useState({});
  const [addCustomerOpen, setAddCustomerOpen] = useState(false);
  const countryData = apiUtils.useApiData("/api/v1/countries");
  const entityTypes = apiUtils.useApiData("/api/v1/relationTypes");

  const fetchCustomers = async () => {
    try {
      const response = await axios.get("/api/v1/entities");
      setTableData(response.data);
    } catch (error) {
      console.error("Error fetching entities:", error);
    }
  };

  useEffect(() => {
    fetchCustomers();
  }, []);

  useEffect(() => {
    if (entityTypes.length > 0) {
      setActiveTab(entityTypes[0].relationTypeId);
    }
  }, [entityTypes]);

  const filteredData = useMemo(() => {
    return tableData.filter((data) => data.typeRelation === activeTab);
  }, [tableData, activeTab]);

  // Fetch document statuses - OPTIMIZED: Single API call voor alle klanten
  useEffect(() => {
    const fetchDocumentStatuses = async () => {
      if (filteredData.length === 0) return;

      try {
        // Nieuwe endpoint: Haal alle document statuses in één keer op
        const response = await axios.get("/api/v1/customerdocuments/documentstatuses");

        // Response is een object met customerId als key en status als value
        // Bijvoorbeeld: { 123: { hasExpired: true, hasExpiring: false }, ... }
        setDocumentStatuses(response.data || {});
      } catch (error) {
        console.error("Error fetching document statuses:", error);
        setDocumentStatuses({});
      }
    };

    fetchDocumentStatuses();
  }, [filteredData]);

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const defaultSortModel = [
    {
      field: "entityNumber", // Specify the field to sort by
      sort: "asc", // Specify the sort direction ('asc' or 'desc')
    },
  ];

  // console.table(tableData);

  const columns = [
    { field: "entityId", headerName: "EntityID", width: 50, hide: true },
    { field: "entityNumber", headerName: "EntityNumber", flex: 0.5 },
    { field: "name", headerName: "Name", flex: 1.5 },
    {
      field: "countryCode",
      headerName: "Country",
      flex: 0.5,
      valueGetter: (params) => countryData.find((country) => country.countryId === params.row.countryId)?.countryCode || "",
    },
    { field: "generalPhoneNumber", headerName: "General Phone", flex: 0.5 },
    { field: "vat", headerName: "VATNumber", flex: 0.5 },
    {
      field: "documentStatus",
      headerName: "Doc Status",
      flex: 0.5,
      align: "center",
      headerAlign: "center",
      renderCell: (params) => {
        const status = documentStatuses[params.row.entityId];
        if (status === undefined) {
          return <CircularProgress size={16} />;
        }

        if (status.hasExpired) {
          return (
            <Tooltip title="Has expired documents">
              <Chip
                icon={<ErrorIcon />}
                label="Expired"
                size="small"
                variant="outlined"
                sx={{
                  bgcolor: "transparent",
                  color: "#c62828",
                  minWidth: 80,
                  "& .MuiChip-icon": {
                    color: "#c62828",
                  },
                }}
              />
            </Tooltip>
          );
        }
        if (status.hasExpiring) {
          return (
            <Tooltip title="Has expiring documents (within 30 days)">
              <Chip
                icon={<WarningIcon />}
                label="Expiring"
                size="small"
                variant="outlined"
                sx={{
                  bgcolor: "transparent",
                  color: "#f57c00",
                  minWidth: 80,
                  "& .MuiChip-icon": {
                    color: "#f57c00",
                  },
                }}
              />
            </Tooltip>
          );
        }
        return (
          <Tooltip title="All documents valid">
            <Chip
              icon={<CheckCircleIcon />}
              label="OK"
              size="small"
              variant="outlined"
              sx={{
                bgcolor: "transparent",
                color: "#2e7d32",
                minWidth: 80,
                "& .MuiChip-icon": {
                  color: "#2e7d32",
                },
              }}
            />
          </Tooltip>
        );
      },
    },
    {
      field: "actions",
      headerName: "Actions",
      flex: 0.5,
      renderCell: (params) => {
        return (
          <Box>
            <IconButton component={Link} to={getCustomerOverviewPath(params.row)}>
              <VisibilityIcon />
            </IconButton>
          </Box>
        );
      },
    },
  ];

  return (
    <React.Fragment>
      <Helmet title="Customers" />
      <Grid justifyContent="space-between" container spacing={10}>
        <Grid item>
          <Typography variant="h3" gutterBottom display="inline">
            Customers
          </Typography>

          <Breadcrumbs aria-label="Breadcrumb" mt={2}>
            <Link component={NavLink} to="/">
              Dashboard
            </Link>
            <Link component={NavLink} to="/">
              Pages
            </Link>
            <Typography>Customers</Typography>
          </Breadcrumbs>
        </Grid>
      </Grid>

      <Divider my={6} />
      <Card mb={6}>
        <CardContent
          pb={1}
          sx={{
            marginBottom: 5,
          }}
        >
          <Typography variant="h6" gutterBottom>
            Customers Table
          </Typography>
          <Typography variant="body2" gutterBottom>
            In this table you can search and view customers. You can use the below options to customize the customer grid. Test.
          </Typography>
        </CardContent>
        <Grid container spacing={6}>
          <Grid item xs={12}>
            {entityTypes && (
              <Tabs value={activeTab} onChange={handleTabChange} aria-label="customer data tabs">
                {entityTypes.map((type) => (
                  <Tab key={type.relationTypeId} label={type.shortDescription} value={type.relationTypeId} />
                ))}
              </Tabs>
            )}
            <Box sx={{ marginBottom: "10px", display: "flex", flexDirection: "column", gap: 1, pl: "12px" }}>
              <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 2, alignItems: "center" }}>
                <Typography variant="body2" fontWeight="bold">
                  Doc Status:
                </Typography>
                <Chip
                  icon={<CheckCircleIcon />}
                  label="Valid"
                  size="small"
                  variant="outlined"
                  sx={{
                    bgcolor: "transparent",
                    color: "#2e7d32",
                    "& .MuiChip-icon": {
                      color: "#2e7d32",
                    },
                  }}
                />
                <Chip
                  icon={<WarningIcon />}
                  label="Expiring"
                  size="small"
                  variant="outlined"
                  sx={{
                    bgcolor: "transparent",
                    color: "#f57c00",
                    "& .MuiChip-icon": {
                      color: "#f57c00",
                    },
                  }}
                />
                <Chip
                  icon={<ErrorIcon />}
                  label="Expired"
                  size="small"
                  variant="outlined"
                  sx={{
                    bgcolor: "transparent",
                    color: "#c62828",
                    "& .MuiChip-icon": {
                      color: "#c62828",
                    },
                  }}
                />
              </Box>
              <Box sx={{ display: "flex", alignItems: "center", width: "100%" }}>
                {canCreateCustomer && (
                <Button
                  variant="contained"
                  color="primary"
                  onClick={() => setAddCustomerOpen(true)}
                  aria-label="Add Entity"
                >
                  Add Entity
                </Button>
                )}
                <Box sx={{ display: "flex", alignItems: "center", marginLeft: "auto" }}>
                  <DataGridExport tableData={tableData} columns={columns} />
                </Box>
              </Box>
            </Box>
            <DataGrid
              getRowId={(row) => row.entityId}
              components={{ Toolbar: GridToolbar }}
              density="compact"
              componentsProps={{
                toolbar: {
                  csvOptions: { disableToolbarButton: false },
                  printOptions: { disableToolbarButton: false },
                  showQuickFilter: true,
                  quickFilterProps: { debounceMs: 250 },
                },
              }}
              experimentalFeatures={{ newEditingApi: true }}
              rows={filteredData}
              columns={columns}
              pageSize={15}
              sortModel={defaultSortModel}
              onRowDoubleClick={(params) => navigate(getCustomerOverviewPath(params.row))}
              getRowClassName={(params) => {
                const status = documentStatuses[params.row.entityId];
                if (!status) return "";
                if (status.hasExpired) return "row-expired";
                if (status.hasExpiring) return "row-expiring";
                return "";
              }}
              sx={{
                height: 667,
                width: "100%",
                borderRadius: 1,
                backgroundColor: "background.paper",
                boxShadow: 2,
                "& .MuiDataGrid-row": {
                  cursor: "pointer",
                },
                "& .MuiDataGrid-cell:hover": {
                  color: "primary.main",
                },
                "& .row-expired": {
                  backgroundColor: "#ffebee !important",
                  "&:hover": { backgroundColor: "#ffcdd2 !important" },
                  // In dark mode, make text dark gray instead of white
                  ...(theme.palette.mode === "dark" && {
                    color: "#424242 !important",
                    "& .MuiDataGrid-cell": {
                      color: "#424242 !important",
                    },
                  }),
                },
                "& .row-expiring": {
                  backgroundColor: "#fff3e0 !important",
                  "&:hover": { backgroundColor: "#ffe0b2 !important" },
                  // In dark mode, make text dark gray instead of white
                  ...(theme.palette.mode === "dark" && {
                    color: "#424242 !important",
                    "& .MuiDataGrid-cell": {
                      color: "#424242 !important",
                    },
                  }),
                },
                marginTop: 0,
              }}
            />
          </Grid>
        </Grid>
      </Card>

      <AddCustomerDialog
        open={addCustomerOpen}
        onClose={() => setAddCustomerOpen(false)}
        onSuccess={fetchCustomers}
      />
    </React.Fragment>
  );
};

export default CustomerDataGrid;
